const GIFT_SLIDER_DRAG_THRESHOLD = 6;
const GIFT_SLIDER_MAX_FRAME_MS = 50;
const GIFT_SLIDER_MAX_SETS = 12;
const GIFT_SLIDER_MAX_VELOCITY = 4;
const GIFT_SLIDER_MIN_VELOCITY = 0.02;
const GIFT_SLIDER_FRICTION = 0.94;
const GIFT_SLIDER_SMOOTHING = 0.3;
const GIFT_SLIDER_STALE_MOVE_MS = 100;
const GIFT_SLIDER_ONE_UP_RATIO = 0.5;
const GIFT_SLIDER_WHEEL_LINE_PX = 16;
const GIFT_SLIDER_WHEEL_IDLE_MS = 140;

const giftSliders = new WeakMap();

const createGiftSlider = (track) => {
  giftSliders.get(track)?.destroy();

  const cardSelector = ':scope > [data-gift-slider-card]';
  const originals = [...track.querySelectorAll(`${cardSelector}:not([data-gift-slider-clone])`)];
  const viewport = track.closest('[data-gift-slider-viewport]');
  if (!originals.length || !viewport) return;

  const controller = new AbortController();
  const { signal } = controller;
  const count = originals.length;

  let setWidth = 0;
  let offset = 0;
  let dragging = false;
  let dragMoved = false;
  let dragPointer = null;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragStartOffset = 0;
  let lastMoveX = 0;
  let lastMoveTime = 0;
  let velocity = 0;
  let momentumRaf = 0;
  let momentumTime = 0;
  let measureRaf = 0;
  let wheelIdleTimer = 0;
  let lastViewportWidth = 0;

  // CSS declares the transform; JS only supplies the offset scalar.
  const render = () => {
    track.style.setProperty('--gift-slider-offset', `${offset}px`);
  };

  const wrap = (value) => (setWidth > 0 ? ((value % setWidth) + setWidth) % setWidth : 0);

  const removeClones = () => {
    for (const clone of track.querySelectorAll(':scope > [data-gift-slider-clone]')) clone.remove();
  };

  const addSet = () => {
    const fragment = document.createDocumentFragment();

    for (const card of originals) {
      const clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-gift-slider-clone', '');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');

      for (const image of clone.querySelectorAll('img')) {
        image.loading = 'eager';
        image.fetchPriority = 'low';
        image.alt = '';
      }

      for (const focusable of clone.querySelectorAll('a, button, input, select, textarea, [tabindex]')) {
        focusable.setAttribute('tabindex', '-1');
      }

      fragment.append(clone);
    }

    track.append(fragment);
  };

  // Measured from live positions so the flex gap is included exactly.
  const measureSetWidth = () => {
    const cards = [...track.querySelectorAll(cardSelector)];
    if (cards.length <= count) return 0;
    return cards[count].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
  };

  // A one-up carousel starts with its first card centred, so the previous card's tail
  // shows in the gap on the left. A multi-card row starts flush.
  const startInset = () => {
    const cardWidth = originals[0].getBoundingClientRect().width;
    const room = viewport.clientWidth - cardWidth;
    if (room <= 0) return 0;
    return cardWidth / viewport.clientWidth > GIFT_SLIDER_ONE_UP_RATIO ? room / 2 : 0;
  };

  const build = () => {
    removeClones();
    addSet();

    setWidth = measureSetWidth();
    if (setWidth <= 0) {
      render();
      return;
    }

    const needed = Math.min(
      GIFT_SLIDER_MAX_SETS,
      Math.ceil(viewport.clientWidth / setWidth) + 1
    );
    for (let i = 1; i < needed; i += 1) addSet();

    offset = wrap(-startInset());
    lastViewportWidth = viewport.clientWidth;
    render();
  };

  const stopMomentum = () => {
    if (!momentumRaf) return;
    cancelAnimationFrame(momentumRaf);
    momentumRaf = 0;
    momentumTime = 0;
    track.classList.remove('is-animating');
  };

  const momentumStep = (now) => {
    const elapsed = momentumTime ? Math.min(now - momentumTime, GIFT_SLIDER_MAX_FRAME_MS) : 0;
    momentumTime = now;

    if (elapsed > 0) {
      offset = wrap(offset + velocity * elapsed);
      velocity *= GIFT_SLIDER_FRICTION ** (elapsed / 16);
      render();
    }

    if (Math.abs(velocity) < GIFT_SLIDER_MIN_VELOCITY) {
      velocity = 0;
      stopMomentum();
      return;
    }

    momentumRaf = requestAnimationFrame(momentumStep);
  };

  const startMomentum = () => {
    if (Math.abs(velocity) < GIFT_SLIDER_MIN_VELOCITY) {
      velocity = 0;
      return;
    }
    stopMomentum();
    momentumTime = 0;
    track.classList.add('is-animating');
    momentumRaf = requestAnimationFrame(momentumStep);
  };

  const onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    stopMomentum();
    dragging = true;
    dragMoved = false;
    dragPointer = event.pointerId;
    dragStartX = event.clientX;
    dragStartY = event.clientY;
    dragStartOffset = offset;
    lastMoveX = event.clientX;
    lastMoveTime = event.timeStamp;
    velocity = 0;
    viewport.classList.add('is-dragging');
    track.classList.add('is-animating');
  };

  const onPointerMove = (event) => {
    if (!dragging || event.pointerId !== dragPointer) return;

    const dx = event.clientX - dragStartX;

    // Axis lock: decide once, on the first move past the threshold. A predominantly
    // vertical gesture belongs to the page, so bail out instead of nudging the track.
    if (!dragMoved) {
      const dy = event.clientY - dragStartY;
      if (Math.abs(dx) < GIFT_SLIDER_DRAG_THRESHOLD && Math.abs(dy) < GIFT_SLIDER_DRAG_THRESHOLD) return;

      if (Math.abs(dy) >= Math.abs(dx)) {
        dragging = false;
        dragPointer = null;
        velocity = 0;
        viewport.classList.remove('is-dragging');
        track.classList.remove('is-animating');
        return;
      }

      dragMoved = true;

      // A mouse pointer has no implicit capture, so tracking would stop the moment the
      // cursor leaves the viewport. Touch already has it, and capturing a touch pointer
      // re-fires lostpointercapture and kills the swipe. Claiming it only after the
      // threshold keeps a plain click's target intact.
      if (event.pointerType === 'mouse') viewport.setPointerCapture(event.pointerId);
    }

    const elapsed = event.timeStamp - lastMoveTime;
    if (elapsed > 0) {
      const stepX = event.clientX - lastMoveX;
      const instant = Math.max(
        -GIFT_SLIDER_MAX_VELOCITY,
        Math.min(GIFT_SLIDER_MAX_VELOCITY, -stepX / elapsed)
      );
      velocity += (instant - velocity) * GIFT_SLIDER_SMOOTHING;
      lastMoveX = event.clientX;
      lastMoveTime = event.timeStamp;
    }

    offset = wrap(dragStartOffset - dx);
    render();
  };

  const onPointerUp = (event) => {
    if (!dragging || event.pointerId !== dragPointer) return;

    dragging = false;
    dragPointer = null;
    if (viewport.hasPointerCapture?.(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
    viewport.classList.remove('is-dragging');

    // A pause before release means the user stopped: don't fling.
    if (event.timeStamp - lastMoveTime > GIFT_SLIDER_STALE_MOVE_MS) velocity = 0;

    if (velocity === 0) {
      track.classList.remove('is-animating');
      return;
    }

    startMomentum();
  };

  // Trackpad two-finger swipe and shift+wheel. Vertical intent is left to the page.
  const onWheel = (event) => {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    let delta = event.deltaX;
    if (event.deltaMode === 1) delta *= GIFT_SLIDER_WHEEL_LINE_PX;
    else if (event.deltaMode === 2) delta *= viewport.clientWidth;

    event.preventDefault();
    stopMomentum();

    offset = wrap(offset + delta);
    render();

    track.classList.add('is-animating');
    clearTimeout(wheelIdleTimer);
    wheelIdleTimer = setTimeout(() => {
      track.classList.remove('is-animating');
      wheelIdleTimer = 0;
    }, GIFT_SLIDER_WHEEL_IDLE_MS);
  };

  const remeasure = () => {
    if (dragging || viewport.clientWidth === lastViewportWidth) return;
    build();
  };

  viewport.addEventListener('pointerdown', onPointerDown, { signal });
  viewport.addEventListener('pointermove', onPointerMove, { signal });
  viewport.addEventListener('pointerup', onPointerUp, { signal });
  viewport.addEventListener('pointercancel', onPointerUp, { signal });
  viewport.addEventListener('wheel', onWheel, { passive: false, signal });
  viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
  viewport.addEventListener('click', (event) => {
    if (!dragMoved) return;
    event.preventDefault();
    event.stopPropagation();
    dragMoved = false;
  }, { capture: true, signal });

  const resizeObserver = new ResizeObserver(() => {
    cancelAnimationFrame(measureRaf);
    measureRaf = requestAnimationFrame(remeasure);
  });
  resizeObserver.observe(viewport);

  window.addEventListener('load', remeasure, { once: true, signal });

  const destroy = () => {
    controller.abort();
    resizeObserver.disconnect();
    cancelAnimationFrame(measureRaf);
    clearTimeout(wheelIdleTimer);
    stopMomentum();
    removeClones();
    viewport.classList.remove('is-dragging');
    track.classList.remove('is-animating');
    track.style.removeProperty('--gift-slider-offset');
    giftSliders.delete(track);
  };

  giftSliders.set(track, { destroy, remeasure });
  build();
};

const initGiftSliders = (root = document) => {
  for (const track of root.querySelectorAll('[data-gift-slider-track]')) createGiftSlider(track);
};

document.addEventListener('DOMContentLoaded', () => initGiftSliders());

document.addEventListener('shopify:section:load', (event) => initGiftSliders(event.target));

document.addEventListener('shopify:section:unload', (event) => {
  for (const track of event.target.querySelectorAll('[data-gift-slider-track]')) {
    giftSliders.get(track)?.destroy();
  }
});
